import type Phaser from "phaser";
import { CANVAS_COLOURS, cssColour, pathLegs, type PathPoint } from "daedalus-core";
import { TAG_FONT_FAMILY, TAG_FONT_SIZE } from "./activation-view.ts";

/**
 * The paths a selected thing's path fields hold (ADR-136 decision 2), drawn in gold over the map: numbered points
 * joined by a line, with an arrowhead on each leg (one way where the path loops, both ways where it walks out and
 * back), the first point's plate square and the rest round-cornered, and the closing leg of a loop dashed. The path
 * being edited is drawn strongly, the others quieter. Line widths and plates keep their screen size at any zoom.
 * One `Graphics` for the lines and one for the plates, with the numbers pooled as `Text`, redrawn only when the
 * paths or the zoom change.
 */

/**
 * The line's width in screen pixels, and the dark keyline either side of it.
 */
export const PATH_LINE_PX = 3;

/**
 * The keyline's extra width on each side, in screen pixels.
 */
export const PATH_KEYLINE_PX = 1;

/**
 * A point's plate in screen pixels.
 */
export const PATH_PLATE_PX = 18;

/**
 * An arrowhead's length and half-width in screen pixels.
 */
export const PATH_ARROW_PX = 10;

/**
 * The opacity of a path shown and of the path being edited.
 */
export const PATH_ALPHA = { shown: 0.65, editing: 1 } as const;

/**
 * The dash and gap of a loop's closing leg in screen pixels.
 */
export const PATH_DASH_PX = 5;

/**
 * One path to draw: its thing's anchor in world pixels, a cell's size in pixels, its points in order, whether it
 * loops and whether the map is editing it.
 */
export interface FieldPathArea {
  readonly anchor: { readonly x: number; readonly y: number };
  readonly cellPixels: number;
  readonly points: readonly PathPoint[];
  readonly closed: boolean;
  readonly editing: boolean;
}

/**
 * A point's centre in world pixels.
 *
 * @param area The path.
 * @param point The `[column, rowsUp]` cell.
 * @returns The centre of the cell.
 */
export function pathCentre(area: Pick<FieldPathArea, "anchor" | "cellPixels">, point: PathPoint): { readonly x: number; readonly y: number } {
  return { x: area.anchor.x + (point[0] + 0.5) * area.cellPixels, y: area.anchor.y - (point[1] + 0.5) * area.cellPixels };
}

/**
 * The arrowheads of one leg, as triangles of three corners in world pixels: one at the leg's middle pointing along
 * it for a loop, a pair back to back at its middle for a path that walks out and back. A leg shorter than its
 * arrowhead has none.
 *
 * @param from The leg's start in world pixels.
 * @param to The leg's end in world pixels.
 * @param both True for a pair pointing both ways.
 * @param px The size of a screen pixel in world pixels.
 * @returns The triangles as `[x0, y0, x1, y1, x2, y2]`.
 */
export function legArrows(from: { readonly x: number; readonly y: number }, to: { readonly x: number; readonly y: number }, both: boolean, px: number): [number, number, number, number, number, number][] {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const length = Math.hypot(dx, dy);
  const size = PATH_ARROW_PX * px;
  if (length < size * (both ? 3 : 2)) {
    return [];
  }
  const ux = dx / length;
  const uy = dy / length;
  const head = (cx: number, cy: number, sign: 1 | -1): [number, number, number, number, number, number] => {
    const tipX = cx + (ux * size * sign) / 2;
    const tipY = cy + (uy * size * sign) / 2;
    const backX = cx - (ux * size * sign) / 2;
    const backY = cy - (uy * size * sign) / 2;
    return [tipX, tipY, backX - uy * size * 0.6, backY + ux * size * 0.6, backX + uy * size * 0.6, backY - ux * size * 0.6];
  };
  const mx = (from.x + to.x) / 2;
  const my = (from.y + to.y) / 2;
  if (!both) {
    return [head(mx, my, 1)];
  }
  const gap = size * 0.6;
  return [head(mx + ux * gap, my + uy * gap, 1), head(mx - ux * gap, my - uy * gap, -1)];
}

/**
 * The paths of one scene.
 */
export class FieldPathView {
  private readonly lines: Phaser.GameObjects.Graphics;
  private readonly plates: Phaser.GameObjects.Graphics;
  private readonly labels: Phaser.GameObjects.Text[] = [];
  private last = "";

  /**
   * @param scene Scene to draw into.
   * @param depth Depth over the map's layers; the plates and numbers draw just above.
   */
  public constructor(
    private readonly scene: Phaser.Scene,
    private readonly depth: number,
  ) {
    this.lines = scene.add.graphics().setDepth(depth);
    this.plates = scene.add.graphics().setDepth(depth + 1);
  }

  /**
   * Draws the paths, or clears them, when they or the zoom changed since the last call.
   *
   * @param areas The paths.
   * @param zoom The camera's zoom.
   * @returns True when a redraw happened.
   */
  public sync(areas: readonly FieldPathArea[], zoom: number): boolean {
    const signature = `${zoom}|${areas.map((a) => `${a.anchor.x},${a.anchor.y},${a.cellPixels},${a.closed ? 1 : 0},${a.editing ? 1 : 0}:${a.points.map(([c, r]) => `${c},${r}`).join(" ")}`).join(";")}`;
    if (signature === this.last) {
      return false;
    }
    this.last = signature;
    this.lines.clear();
    this.plates.clear();
    let used = 0;
    const px = 1 / zoom;
    for (const area of areas) {
      this.drawLines(area, px);
      used = this.drawPlates(area, px, used);
    }
    for (let at = used; at < this.labels.length; at += 1) {
      this.labels[at]?.setVisible(false);
    }
    return true;
  }

  /**
   * Removes the drawing.
   */
  public destroy(): void {
    this.lines.destroy();
    this.plates.destroy();
    for (const label of this.labels) {
      label.destroy();
    }
  }

  /**
   * Draws a path's legs: a dark keyline, the gold line over it, the closing leg dashed, and the arrowheads.
   *
   * @param area The path.
   * @param px The size of a screen pixel in world pixels.
   */
  private drawLines(area: FieldPathArea, px: number): void {
    const g = this.lines;
    const gold = CANVAS_COLOURS.fieldPath;
    const keyline = CANVAS_COLOURS.activationKeyline;
    const alpha = area.editing ? PATH_ALPHA.editing : PATH_ALPHA.shown;
    const legs = pathLegs(area.points, area.closed);
    const closing = area.closed && area.points.length > 2 ? legs.length - 1 : -1;
    legs.forEach(([from, to], at) => {
      const a = pathCentre(area, from);
      const b = pathCentre(area, to);
      g.lineStyle((PATH_LINE_PX + 2 * PATH_KEYLINE_PX) * px, keyline.rgb, keyline.alpha * alpha);
      this.segment(a, b, at === closing, px);
      g.lineStyle(PATH_LINE_PX * px, gold.rgb, gold.alpha * alpha);
      this.segment(a, b, at === closing, px);
      g.fillStyle(gold.rgb, gold.alpha * alpha);
      for (const [x0, y0, x1, y1, x2, y2] of legArrows(a, b, !area.closed, px)) {
        g.fillTriangle(x0, y0, x1, y1, x2, y2);
      }
    });
  }

  /**
   * Strokes one leg, whole or dashed.
   *
   * @param a The start in world pixels.
   * @param b The end in world pixels.
   * @param dashed Whether to dash it.
   * @param px The size of a screen pixel in world pixels.
   */
  private segment(a: { readonly x: number; readonly y: number }, b: { readonly x: number; readonly y: number }, dashed: boolean, px: number): void {
    const g = this.lines;
    if (!dashed) {
      g.lineBetween(a.x, a.y, b.x, b.y);
      return;
    }
    const length = Math.hypot(b.x - a.x, b.y - a.y);
    const step = PATH_DASH_PX * px;
    for (let from = 0; from < length; from += 2 * step) {
      const to = Math.min(length, from + step);
      g.lineBetween(a.x + ((b.x - a.x) * from) / length, a.y + ((b.y - a.y) * from) / length, a.x + ((b.x - a.x) * to) / length, a.y + ((b.y - a.y) * to) / length);
    }
  }

  /**
   * Draws a path's point plates and numbers.
   *
   * @param area The path.
   * @param px The size of a screen pixel in world pixels.
   * @param used How many labels earlier paths used.
   * @returns How many labels are used after this path.
   */
  private drawPlates(area: FieldPathArea, px: number, used: number): number {
    const g = this.plates;
    const gold = CANVAS_COLOURS.fieldPath;
    const keyline = CANVAS_COLOURS.activationKeyline;
    const alpha = area.editing ? PATH_ALPHA.editing : PATH_ALPHA.shown;
    const size = PATH_PLATE_PX * px;
    let next = used;
    area.points.forEach((point, at) => {
      const { x, y } = pathCentre(area, point);
      const radius = at === 0 ? 0 : size * 0.3;
      g.fillStyle(keyline.rgb, keyline.alpha * alpha);
      this.plate(x - size / 2 - PATH_KEYLINE_PX * px, y - size / 2 - PATH_KEYLINE_PX * px, size + 2 * PATH_KEYLINE_PX * px, radius + PATH_KEYLINE_PX * px);
      g.fillStyle(gold.rgb, gold.alpha * alpha);
      this.plate(x - size / 2, y - size / 2, size, radius);
      let label = this.labels[next];
      if (label === undefined) {
        label = this.scene.add.text(0, 0, "", { fontFamily: TAG_FONT_FAMILY, fontSize: TAG_FONT_SIZE, fontStyle: "500", color: cssColour(keyline) }).setOrigin(0.5, 0.5).setDepth(this.depth + 2);
        this.labels.push(label);
      }
      const text = String(at + 1);
      if (label.text !== text) {
        label.setText(text);
      }
      label.setScale(px).setPosition(x, y).setAlpha(alpha).setVisible(true);
      next += 1;
    });
    return next;
  }

  /**
   * Fills one plate, square or with rounded corners.
   *
   * @param x Left in world pixels.
   * @param y Top in world pixels.
   * @param size Width and height in world pixels.
   * @param radius Corner radius in world pixels, 0 for square.
   */
  private plate(x: number, y: number, size: number, radius: number): void {
    if (radius <= 0) {
      this.plates.fillRect(x, y, size, size);
    } else {
      this.plates.fillRoundedRect(x, y, size, size, radius);
    }
  }
}
